import React, { Suspense, useCallback, useMemo, useState } from 'react';

import MapBoundary from './MapBoundary';

/**
 * THE LAZY BOUNDARY of the map chunk (KPR-392). The one module of
 * `src/components/map/` that code outside the folder may import, and it
 * imports nothing of the chunk statically: the canvas is a React.lazy over
 * a dynamic import of ItemsMapCanvas, which webpack emits as its own file with
 * MapLibre, the PMTiles reader, the basemap style and MapLibre's CSS inside.
 * A list that never shows its map downloads none of it.
 *
 *   <LazyItemsMap
 *     waiting={<node shown while the chunk downloads>}
 *     renderFailure={retry => <node with a Retry that calls retry>}
 *     {...ItemsMapCanvas props}
 *   />
 *
 * A chunk that fails to download is caught by MapBoundary (React only, in the
 * main bundle) and drawn by `renderFailure`; its Retry asks for the chunk
 * again with a fresh React.lazy — a rejected lazy remembers its failure for
 * good, and webpack forgets a failed chunk so the next import() asks again.
 */
const load = () => import(/* webpackChunkName: "map" */ './ItemsMapCanvas');

export default function LazyItemsMap({ waiting = null, renderFailure, ...props }) {
	const [attempt, setAttempt] = useState(0);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- a new attempt is a new lazy
	const Canvas = useMemo(() => React.lazy(load), [attempt]);
	const retry = useCallback(() => setAttempt(count => count + 1), []);
	return (
		<MapBoundary key={attempt} onRetry={retry} fallback={renderFailure}>
			<Suspense fallback={waiting}>
				<Canvas {...props} />
			</Suspense>
		</MapBoundary>
	);
}

/** Start downloading the map chunk before it is drawn. */
export const preloadItemsMap = load;
